<template>
  <div class="management-additional-analysis-cards__description">
    <p>
      Количество людей на каждом из уровней (низкий, средний, высокий, высший) развития ЭИ по общему ЭИ, двум доменам, четырём ветвям и 10 разделам методики ТЭИ.
    </p>
  </div>

  <section class="management-additional-analysis-cards">
    <section
      v-for="group in groupedCards"
      :key="group.key"
      class="management-additional-analysis-cards__group"
    >
      <h2
        v-if="group.title"
        class="management-additional-analysis-cards__group-title"
      >
        {{ group.title }}
      </h2>

      <div class="management-additional-analysis-cards__grid">
        <ManagementAdditionalAnalysisCard
          v-for="card in group.cards"
          :key="card.key"
          :title="card.title"
          :average-value="card.averageValue"
          :levels="card.levels"
        />
      </div>
    </section>
  </section>
</template>

<script>
import { computed } from 'vue'
import ManagementAdditionalAnalysisCard from './ManagementAdditionalAnalysisCard.vue'

const LEVELS = [
  {
    key: 'professional',
    label: 'Высший',
    color: '#21AC0C',
  },
  {
    key: 'high',
    label: 'Высокий',
    color: '#99DA6E',
  },
  {
    key: 'base',
    label: 'Средний',
    color: '#DDD71D',
  },
  {
    key: 'low',
    label: 'Низкий',
    color: '#FF9F43',
  },
]

const DEFAULT_SECTION_TITLES = {
  total: 'Общий ЭИ',
  domain_1: 'Опытный ЭИ',
  domain_2: 'Стратегический ЭИ',
  branch_1: 'Распознавание эмоций',
  branch_2: 'Использование эмоций для решения задач',
  branch_3: 'Анализ эмоций',
  branch_4: 'Управление эмоциями',
  section_1: 'Распознавание эмоций в лицах',
  section_5: 'Распознавание эмоций в среде',
  section_9a: 'Распознавание эмоций в контексте ситуаций',
  section_2: 'Фасилитация/применение эмоций в действиях',
  section_6: 'Эмпатия',
  section_3: 'Анализ изменений эмоций',
  section_7: 'Анализ соединения эмоций в чувства',
  section_9b: 'Анализ динамики эмоций в контексте ситуаций',
  section_4: 'Регуляция своих эмоций',
  section_8: 'Влияние на эмоции других',
}

const GROUPS = [
  {
    key: 'total',
    title: '',
    sections: ['total'],
  },
  {
    key: 'domains',
    title: 'Домены',
    sections: ['domain_1', 'domain_2'],
  },
  {
    key: 'branches',
    title: 'Ветви',
    sections: ['branch_1', 'branch_2', 'branch_3', 'branch_4'],
  },
  {
    key: 'sections',
    title: 'Разделы',
    sections: [
      'section_1',
      'section_5',
      'section_9a',
      'section_2',
      'section_6',
      'section_3',
      'section_7',
      'section_9b',
      'section_4',
      'section_8',
    ],
  },
]

export default {
  name: 'ManagementAdditionalAnalysisCards',

  components: {
    ManagementAdditionalAnalysisCard,
  },

  props: {
    users: {
      type: Array,
      default: () => [],
    },
    sectionTitles: {
      type: Object,
      default: () => ({}),
    },
  },

  setup(props) {
    const mergedSectionTitles = computed(() => {
      return {
        ...DEFAULT_SECTION_TITLES,
        ...props.sectionTitles,
      }
    })

    const toNumber = (value) => {
      const number = Number(value)
      return Number.isFinite(number) ? number : null
    }

    const buildCardModel = (sectionKey) => {
      const stats = {
        low: 0,
        base: 0,
        high: 0,
        professional: 0,
        sum: 0,
        count: 0,
      }

      props.users.forEach((user) => {
        const value = toNumber(user?.[sectionKey])

        if (value === null) {
          return
        }

        stats.sum += value
        stats.count += 1

        if (value >= 0 && value <= 89) {
          stats.low += 1
          return
        }

        if (value >= 90 && value <= 109) {
          stats.base += 1
          return
        }

        if (value >= 110 && value <= 129) {
          stats.high += 1
          return
        }

        if (value >= 130 && value <= 150) {
          stats.professional += 1
        }
      })

      return {
        key: sectionKey,
        title: mergedSectionTitles.value[sectionKey] ?? sectionKey,
        averageValue: stats.count ? Math.round(stats.sum / stats.count) : 0,
        levels: LEVELS.map((level) => ({
          key: level.key,
          label: level.label,
          color: level.color,
          value: stats[level.key],
        })),
      }
    }

    const groupedCards = computed(() => {
      return GROUPS.map((group) => ({
        ...group,
        cards: group.sections.map((sectionKey) => buildCardModel(sectionKey)),
      }))
    })

    return {
      groupedCards,
    }
  },
}
</script>

<style scoped lang="scss">
.management-additional-analysis-cards {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.management-additional-analysis-cards__description {
  max-width: 800px;
  margin-bottom: 20px;

  p {
    margin: 0;
    font-weight: 400;
    font-size: 14px;
    line-height: 120%;
    color: rgba(0, 0, 0, 0.88);
  }
}

.management-additional-analysis-cards__group {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.management-additional-analysis-cards__group-title {
  margin: 0;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.85);
}

.management-additional-analysis-cards__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

@media (max-width: $mobile-breakpoint) {
  .management-additional-analysis-cards__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
